import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type AdminCandidateStatus = 'active' | 'under_review' | 'suspended' | 'banned';

export type CandidateAuditEvent = {
  id: string;
  action: string;
  actor: string;
  createdAt: string;
  note?: string;
};

export type AdminCandidate = {
  id: string;
  name: string;
  email: string;
  headline: string;
  location: string;
  experienceYears: number;
  status: AdminCandidateStatus;
  lastActive: string;
  joinedAt: string;
  flags: number;
  profileCompleteness: number;
  returner: boolean;
  skills: string[];
  phone?: string;
  bio?: string;
  auditLog: CandidateAuditEvent[];
};

export type CandidateFilters = {
  status?: AdminCandidateStatus;
  query?: string;
};

const mockCandidates: AdminCandidate[] = [
  {
    id: 'cand-1001',
    name: 'Ava Patel',
    email: 'ava.patel@example.com',
    headline: 'Care Team Lead · 10 years',
    location: 'London, UK',
    experienceYears: 10,
    status: 'active',
    lastActive: '2025-11-15T08:05:00.000Z',
    joinedAt: '2024-03-10T09:00:00.000Z',
    flags: 0,
    profileCompleteness: 92,
    returner: true,
    skills: ['Team leadership', 'Safeguarding', 'Elder care'],
    bio: 'Inclusive care leader mentoring hybrid teams across London boroughs.',
    auditLog: [
      { id: 'aud-1', action: 'profile_reviewed', actor: 'admin:mara', createdAt: '2025-10-01T12:22:00.000Z', note: 'Verified employment history.' },
    ],
  },
  {
    id: 'cand-1002',
    name: 'Leo Martins',
    email: 'leo.martins@example.com',
    headline: 'Community Support Worker',
    location: 'Manchester, UK',
    experienceYears: 4,
    status: 'under_review',
    lastActive: '2025-11-14T17:12:00.000Z',
    joinedAt: '2025-01-04T12:00:00.000Z',
    flags: 2,
    profileCompleteness: 74,
    returner: false,
    skills: ['Community outreach', 'Medication support'],
    auditLog: [
      { id: 'aud-5', action: 'flag_received', actor: 'system', createdAt: '2025-11-13T09:40:00.000Z', note: 'Employer flagged profile for inconsistent dates.' },
    ],
  },
  {
    id: 'cand-1003',
    name: 'Ivy Osei',
    email: 'ivy.osei@example.com',
    headline: 'Returner Programme Coach',
    location: 'Remote (UK)',
    experienceYears: 8,
    status: 'suspended',
    lastActive: '2025-10-30T15:45:00.000Z',
    joinedAt: '2023-06-18T14:30:00.000Z',
    flags: 3,
    profileCompleteness: 81,
    returner: true,
    skills: ['Programme design', 'Career coaching'],
    auditLog: [
      { id: 'aud-9', action: 'suspended', actor: 'admin:ella', createdAt: '2025-10-31T11:00:00.000Z', note: 'Temporarily suspended pending ID verification.' },
    ],
  },
];

const filterMockCandidates = (filters: CandidateFilters = {}) => {
  return mockCandidates.filter((candidate) => {
    if (filters.status && candidate.status !== filters.status) return false;
    if (filters.query) {
      const needle = filters.query.toLowerCase();
      const haystack = [candidate.name, candidate.email, candidate.headline, candidate.location, candidate.skills.join(' ')].join(' ').toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
};

export async function fetchAdminCandidates(filters: CandidateFilters = {}): Promise<AdminCandidate[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load admin candidates.');
  }
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.query) params.set('q', filters.query);
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/candidates?${params.toString()}`);
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Admin candidates fetch failed (${response.status}): ${text}`);
  }
  const payload = await response.json();
  return (payload?.candidates as AdminCandidate[]) ?? [];
}

export async function updateAdminCandidateStatus(id: string, status: AdminCandidateStatus, note?: string) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update admin candidates.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/candidates/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, note }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Candidate action failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminCandidate;
}

export async function exportAdminCandidateGdpr(id: string): Promise<Blob> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to export candidate GDPR data.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/candidates/${id}/export`);
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Candidate export failed (${response.status}): ${text}`);
  }
  const json = await response.json();
  return new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
}

export async function deleteAdminCandidateGdpr(id: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete candidate GDPR data.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/candidates/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Candidate delete failed (${response.status}): ${text}`);
  }
}
